import { useEffect, useState } from 'react';
import { Send } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { AttachmentPicker } from '@/components/ui/AttachmentPicker';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Textarea } from '@/components/ui/Textarea';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { institutesApi } from '../api/institutes.api';
import {
  EMAIL_ATTACHMENT_LABEL,
  EMAIL_ATTACHMENT_TYPES,
  emailInstituteSchema,
  MAX_EMAIL_ATTACHMENT_MB,
  MAX_EMAIL_ATTACHMENTS,
  MAX_EMAIL_TOTAL_MB,
  type EmailInstitutePayload,
} from '../schemas/email.schema';

export interface EmailInstituteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  institute: {
    uuid: string;
    displayName: string;
    adminContactName?: string | null;
    adminContactEmail: string;
  };
}

type FieldErrors = Partial<Record<keyof EmailInstitutePayload, string>>;

const EMPTY: EmailInstitutePayload = { subject: '', message: '' };

/**
 * Writes to an institute's administrator without leaving the console.
 *
 * The recipient is shown, not chosen: the server always sends to the contact
 * address on the record, so the field a person could mistype does not exist.
 * Replies come back to whoever sent it — the server sets `Reply-To` to them.
 *
 * The draft is kept while the dialog is open and cleared only once the server
 * says the email left, so a failed send loses nothing the person typed.
 */
export function EmailInstituteDialog({ isOpen, onClose, institute }: EmailInstituteDialogProps) {
  const [draft, setDraft] = useState<EmailInstitutePayload>(EMPTY);
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [sendError, setSendError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);

  // A fresh composer each time it opens for a different institute.
  useEffect(() => {
    setDraft(EMPTY);
    setFiles([]);
    setErrors({});
    setSendError(null);
  }, [institute.uuid]);

  function close() {
    if (isSending) return;
    onClose();
  }

  function update(field: keyof EmailInstitutePayload, value: string) {
    setDraft((current) => ({ ...current, [field]: value }));
    // The error goes the moment the field is touched, not on the next submit.
    if (errors[field]) setErrors((current) => ({ ...current, [field]: undefined }));
  }

  async function send() {
    const parsed = emailInstituteSchema.safeParse(draft);
    if (!parsed.success) {
      const fields: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof EmailInstitutePayload;
        if (!fields[field]) fields[field] = issue.message;
      }
      setErrors(fields);
      return;
    }

    setIsSending(true);
    setSendError(null);
    try {
      await institutesApi.emailAdmin(institute.uuid, parsed.data, files);
      toast.success('Email sent', `${institute.adminContactEmail} will see it in their inbox.`);
      setDraft(EMPTY);
      setFiles([]);
      onClose();
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        if (caught.code === 'VALIDATION_ERROR' && caught.fields.length > 0) {
          const fields: FieldErrors = {};
          for (const { field, message } of caught.fields) {
            if (field === 'subject' || field === 'message') fields[field] = message;
          }
          setErrors(fields);
        }
        setSendError(
          caught.code === 'EMAIL_UNAVAILABLE'
            ? 'Email is not set up on this server yet, so nothing was sent. Ask whoever manages the server to add the SMTP settings.'
            : caught.message,
        );
      } else {
        setSendError('Something went wrong. Please try again.');
      }
    } finally {
      setIsSending(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      size="md"
      title={`Email ${institute.displayName}`}
      description={`To ${institute.adminContactName ? `${institute.adminContactName}, ` : ''}${institute.adminContactEmail}. Replies come back to you.`}
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={isSending}>
            Cancel
          </Button>
          <Button
            onClick={() => void send()}
            isLoading={isSending}
            leftIcon={<Send className="h-4 w-4" aria-hidden />}
          >
            Send email
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {sendError && (
          <Alert tone="critical" title="Email not sent">
            {sendError}
          </Alert>
        )}

        <Input
          label="Subject"
          placeholder="Licence renewal for 2026-27"
          maxLength={150}
          value={draft.subject}
          error={errors.subject}
          disabled={isSending}
          onChange={(event) => update('subject', event.target.value)}
        />

        <Textarea
          label="Message"
          rows={8}
          maxLength={5000}
          placeholder="Write your message. It is signed with your name, and the administrator can reply straight to you."
          value={draft.message}
          error={errors.message}
          hint={`${draft.message.length.toLocaleString('en-IN')} / 5,000`}
          disabled={isSending}
          onChange={(event) => update('message', event.target.value)}
        />

        <AttachmentPicker
          label="Attachments (optional)"
          files={files}
          onChange={setFiles}
          acceptedTypes={EMAIL_ATTACHMENT_TYPES}
          acceptedLabel={EMAIL_ATTACHMENT_LABEL}
          maxFiles={MAX_EMAIL_ATTACHMENTS}
          maxFileMb={MAX_EMAIL_ATTACHMENT_MB}
          maxTotalMb={MAX_EMAIL_TOTAL_MB}
          hint={`${EMAIL_ATTACHMENT_LABEL} · up to ${MAX_EMAIL_ATTACHMENTS} files, ${MAX_EMAIL_ATTACHMENT_MB} MB each, ${MAX_EMAIL_TOTAL_MB} MB in all`}
          disabled={isSending}
        />
      </div>
    </Modal>
  );
}
